import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import Badge from '../ui/Badge';

const ToolCard = ({ tool }) => {
  // Check if the hook is identical to the name (case-insensitive) to avoid duplication
  const showHook = tool.hook && (!tool.name || tool.hook.trim().toLowerCase() !== tool.name.trim().toLowerCase());

  return (
    <div className="editorial-card p-5 md:p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex gap-4 items-start mb-4">
        {tool.logo && (
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-brand-raised border border-brand-border flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src={tool.logo} alt={tool.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-grow min-w-0">
          <h3 className="text-[26px] md:text-[32px] font-bold text-brand-text truncate leading-tight">{tool.name}</h3>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tool.badges?.map(badge => (
              <Badge key={badge} variant={badge === 'Trending' ? 'trending' : 'new'}>
                {badge}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      {showHook && <p className="font-bold text-brand-text text-[20px] md:text-[24px] mb-3 leading-snug">{tool.hook}</p>}
      <p className="text-[18px] md:text-[20px] text-brand-secondary mb-6 flex-grow leading-[1.55] whitespace-pre-wrap">{tool.description}</p>

      {/* Deep Links Section */}
      {tool.deepLinks && tool.deepLinks.length > 0 && (
        <div className="mb-6 grid gap-2 pointer-events-auto">
          {tool.deepLinks.map((link, idx) => (
            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-brand-bg border border-brand-border rounded-[8px] hover:border-brand-mint transition-colors group">
              {link.thumbnail && (
                <img src={link.thumbnail} alt={link.title} className="w-10 h-10 rounded-[6px] object-cover bg-brand-surface flex-shrink-0" />
              )}
              <div className="flex items-center gap-2">
                {!link.thumbnail && <SafeIcon icon="Link" className="w-4 h-4 text-brand-muted group-hover:text-brand-mint transition-colors" />}
                <span className="text-[16px] md:text-[18px] font-bold text-brand-text group-hover:text-brand-mint transition-colors line-clamp-2">
                  {link.title || 'Visit Link'}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Details */}
      {(tool.bestFor?.length > 0 || tool.features?.length > 0) && (
        <div className="space-y-5 mb-6">
          {tool.bestFor?.length > 0 && (
            <div>
              <h4 className="text-[14px] md:text-[16px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-2">Best For</h4>
              <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">
                {tool.bestFor.join(' · ')}
              </p>
            </div>
          )}
          {tool.features?.length > 0 && (
            <div>
              <h4 className="text-[14px] md:text-[16px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-2">Features</h4>
              <ul className="space-y-2">
                {tool.features.map((feature, idx) => (
                  <li key={idx} className="text-[18px] md:text-[20px] text-brand-secondary flex items-start gap-2.5">
                    <SafeIcon icon="Check" className="w-4 h-4 text-brand-mint mt-1 flex-shrink-0" />
                    <span className="leading-[1.55]">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Footer Actions */}
      <div className="mt-auto pt-5 border-t border-brand-border flex items-center justify-between pointer-events-auto">
        <div className="flex flex-col">
          <span className="text-[16px] md:text-[18px] font-bold text-brand-text">{tool.pricing || 'Free / Paid'}</span>
          {tool.isAffiliate && (
            <span className="text-[12px] md:text-[14px] text-brand-muted uppercase tracking-wider">Affiliate link</span>
          )}
        </div>
        
        {tool.ctaText && tool.ctaUrl && (
          <a href={tool.ctaUrl} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 rounded-md text-[16px] md:text-[18px] font-bold bg-brand-text text-brand-bg hover:bg-brand-secondary transition-colors flex items-center gap-2">
            {tool.ctaText} <SafeIcon icon="ExternalLink" className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
};

export default ToolCard;